/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';
import { AssetUtils, Episode, Program, Series, TvAsset, Vod } from '@wit/vtv-x-foundation-lib';
import translate from 'services/i18n/translate';
import useAssetTitle from '../../../hooks/useAssetTitle';
import useIsAdultContent from '../../../hooks/useIsAdultContent';

interface Props {
  liveEvent?: Program;
  tvEvent?: TvAsset;
  onDemandEvent?: Vod | Series;
}

function getProgramDetails(isSpecialSeason = false, { liveEvent, tvEvent, onDemandEvent }: Props): JSX.Element | null {
  const noEpisodesFallbackText = '';

  const seasonNumber = (liveEvent || tvEvent || (onDemandEvent as Episode))?.seasonNumber;
  const episodeName = (liveEvent || tvEvent || (onDemandEvent as Episode))?.episodeName || noEpisodesFallbackText;
  const episodeNameElement = <span className="episode-name">{`${episodeName}`}</span>;

  if (isSpecialSeason || !seasonNumber) {
    if (!episodeName) {
      return null;
    }
    return episodeNameElement;
  }

  const seasonNumberLabel = translate.formatString(translate['asset.seasonFormat'], seasonNumber).toString();
  const episodeNumber = (liveEvent || tvEvent || (onDemandEvent as Episode))?.episodeNumber;
  if (!episodeNumber) {
    return (
      <>
        <span>{`${seasonNumberLabel}`}</span>&nbsp;
        {episodeNameElement}
      </>
    );
  }

  const episodeNumberLabel = translate.formatString(translate['asset.episodeFormat'], episodeNumber).toString();

  return (
    <>
      <span>{`${seasonNumberLabel}, ${episodeNumberLabel}`}</span>&nbsp;
      {episodeNameElement}
    </>
  );
}

export default function Header({ liveEvent, tvEvent, onDemandEvent }: Props): JSX.Element {
  const isOnDemandSeriesOrEpisode =
    onDemandEvent && (AssetUtils.isSeries(onDemandEvent) || AssetUtils.isEpisode(onDemandEvent));

  const isSpecialSeason = AssetUtils.isSpecialSeasonNumber(
    liveEvent?.seasonNumber || tvEvent?.seasonNumber || (onDemandEvent as Episode)?.seasonNumber
  );

  const programDetailsElement =
    !onDemandEvent || isOnDemandSeriesOrEpisode
      ? getProgramDetails(isSpecialSeason, { liveEvent, tvEvent, onDemandEvent })
      : null;
  const parentalControlLabel = liveEvent?.parentalRating || tvEvent?.parentalRating || onDemandEvent?.parentalRating;

  const title = useAssetTitle(liveEvent || tvEvent || onDemandEvent, false);
  const isAdultContent = useIsAdultContent(liveEvent || tvEvent || onDemandEvent);

  return (
    <div className="programme-info-header" id="ProgrammeInfo_header">
      {liveEvent && liveEvent.startTime && liveEvent.endTime ? (
        <h5>{`${liveEvent.startTime} - ${liveEvent.endTime}`}</h5>
      ) : null}

      <h2 id="ProgrammeInfo_title" className={tvEvent || onDemandEvent ? 'spaced' : ''}>
        {title}
      </h2>

      {programDetailsElement && !isAdultContent ? (
        <div id="ProgrammeInfo_details" className={`program-details ${tvEvent || onDemandEvent ? 'spaced' : ''}`}>
          {programDetailsElement}
        </div>
      ) : null}

      {parentalControlLabel ? (
        <div
          id="ProgrammeInfo_parentalControlLabel"
          className={`parental-control ${tvEvent || onDemandEvent ? 'no-margin' : ''}`}
        >
          <span>{parentalControlLabel}</span>
        </div>
      ) : null}
    </div>
  );
}
